import {
  BookBookmarkIcon,
  CalendarBlankIcon,
  ChartBarIcon,
  ChartLineIcon,
  CircleIcon,
  CursorClickIcon,
  FileImageIcon,
  FileMagnifyingGlassIcon,
  FileTextIcon,
  FileVideoIcon,
  FilmSlateIcon,
  GearIcon,
  HouseIcon,
  LightningIcon,
  ListIcon,
  PaintBrushIcon,
  PaletteIcon,
  PlayCircleIcon,
  ShoppingBagIcon,
  SquaresFourIcon,
  TextAaIcon,
  ToggleLeftIcon,
  UserCircleIcon,
  UserIcon,
  UserRectangleIcon,
  UserSoundIcon,
  UsersThreeIcon,
} from "@phosphor-icons/react";
import { TFunction } from "i18next";
import { ResourceProps } from "@refinedev/core";
import { FeatureFlags } from "constants/";

type HasRoleFn = (
  subject: string,
  action: string,
  scope?: "ALL" | "ORG" | "MINE" | "NONE"
) => string;

export const getResources = (
  hasRole: HasRoleFn,
  t: TFunction
): ResourceProps[] => [
  {
    name: "media",
    list: "/media",
    meta: {
      label: t("src.App.workSpace"),
      icon: <ListIcon />,
      hasRole: true,
    },
  },
  {
    name: "media_asset_libraries",
    list: "/media/libraries",
    create: "/media/libraries/create",
    show: "/media/libraries/:id",
    edit: "/media/libraries/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.assetLibraries"),
      icon: <CircleIcon />,
    },
  },
  {
    name: "media_dashboard",
    list: "/media/dashboard",
    meta: {
      parent: "media",
      icon: <SquaresFourIcon />,
      label: t("src.App.dashboard"),
      hasRole: true,
      hide: false,
    },
  },
  {
    name: "assets",
    list: "/assets",
    show: "/assets/:id",
    meta: {
      parent: "media",
      icon: <ChartLineIcon />,
      label: t("src.App.stocks"),
      hasRole: true,
      hide: false,
    },
  },
  {
    name: "media_home",
    show: "/home",
    list: "/home",
    meta: {
      parent: "media",
      label: t("src.App.home"),
      icon: <HouseIcon />,
    },
  },
  {
    name: "workspaces",
    list: "/workspaces",
    show: "/workspaces/:id/detail",
    meta: {
      parent: "media",
      label: t("src.App.workspaces"),
      icon: <SquaresFourIcon />,
    },
  },
  {
    name: "media_projects",
    show: "/media/projects/:id",
    list: "/media/projects",
    create: "/media/projects/create",
    edit: "/media/projects/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.projects"),
      icon: <FilmSlateIcon />,
    },
  },
  {
    name: "media_analytics",
    list: "/media/analytics",
    meta: {
      parent: "media",
      label: t("src.App.analytics"),
      icon: <ChartBarIcon />,
    },
  },
  {
    name: "media_brandkits",
    list: "media/brandkits",
    show: "media/brandkits/:id",
    create: "media/brandkits/create",
    edit: "media/brandkits/:id/edit",
    meta: {
      parent: "media",
      icon: <PaletteIcon />,
      label: t("src.App.brandKits"),
    },
  },
  {
    name: "media_contacts",
    show: "/media/contacts/:id",
    list: "/media/contacts",
    create: "/media/contacts/create",
    edit: "/media/contacts/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.contacts"),
      icon: <UserRectangleIcon />,
    },
  },
  {
    name: "media_voices",
    show: "/media/voices/:id",
    list: "/media/voices",
    create: "/media/voices/create",
    edit: "/media/voices/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.voices"),
      icon: <UserSoundIcon />,
      adminOnly: true,
    },
  },
  {
    name: "media_faces",
    show: "/media/faces/:id",
    list: "/media/faces",
    create: "/media/faces/create",
    edit: "/media/faces/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.faces"),
      icon: <UserCircleIcon />,
      adminOnly: true,
    },
  },
  {
    name: "media_presenters",
    list: "/media/presenters",
    meta: {
      parent: "media",
      label: t("src.App.presenters"),
      icon: <UserIcon />,
      adminOnly: true,
    },
  },
  {
    name: "media_ctas",
    show: "/media/ctas/:id",
    list: "/media/ctas",
    create: "/media/ctas/create",
    edit: "/media/ctas/:id",
    meta: {
      parent: "media",
      label: t("src.App.callToActions"),
      hide: true,
      icon: <CursorClickIcon />,
    },
  },
  {
    name: "playbooks",
    list: "/playbooks",
    edit: "/playbooks/:id/edit",
    meta: {
      parent: "media",
      label: t("src.App.playbooks"),
      icon: <BookBookmarkIcon />,
    },
  },
  {
    name: "media_products",
    show: "/media/products",
    list: "/media/products",
    create: "/media/products/create",
    meta: {
      icon: <ShoppingBagIcon />,
      parent: "media",
      label: t("src.App.products"),
      hasRole: true,
    },
  },
  {
    name: "media_documents",
    list: "/media/documents",
    create: "/media/documents/upload",
    show: "/media/documents/:id",
    meta: {
      parent: "media",
      icon: <FileTextIcon />,
      label: t("src.App.documents"),
      hasRole: true,
      hide: true,
    },
  },
  {
    name: "media_media",
    show: "/media/media/:id",
    list: "/media/media",
    meta: {
      parent: "media",
      icon: <PlayCircleIcon />,
      label: t("src.App.media"),
      canDelete: false,
      hide: hasRole("Media.GenerationStep", "show") === "none",
    },
  },
  {
    name: "media_batches",
    list: "/media/batches",
    show: "/media/batches/:id/:key",
    meta: {
      parent: "media",
      icon: <CalendarBlankIcon />,
      label: t("src.App.batches"),
      hasRole: hasRole("Media.Batch", "list"),
    },
  },
  {
    name: "media_assets",
    list: "/media/assets",
    show: "/media/assets/:id",
    meta: {
      parent: "media",
      icon: <FileImageIcon />,
      label: t("src.App.assets"),
      hasRole: hasRole("Media.Asset", "list"),
      hide: hasRole("Media.GenerationStep", "show") === "none",
    },
  },
  {
    name: "media_assets_review",
    show: "/media/assets/review",
    list: "/media/assets/review",
    meta: {
      parent: "media",
      icon: <FileMagnifyingGlassIcon />,
      label: t("src.App.assetsReview"),
      hasRole: hasRole("Media.Asset", "edit"),
      adminOnly: true,
    },
  },
  {
    name: "media_templates",
    list: "/media/templates",
    meta: {
      icon: <FileVideoIcon />,
      parent: "media",
      label: t("src.App.templates"),
      hasRole: hasRole("Media.Template", "show"),
      hide: false,
    },
  },
  {
    name: "media_pronunciations",
    show: "/media/pronunciations",
    list: "/media/pronunciations",
    meta: {
      icon: <TextAaIcon />,
      parent: "media",
      label: t("src.App.pronunciations"),
      hasRole: hasRole("Media.Pronunciation", "show"),
      adminOnly: true,
    },
  },
  {
    name: "users",
    show: "/users",
    list: "/users",
    meta: {
      icon: <UsersThreeIcon />,
      label: t("src.App.users"),
      hasRole: hasRole("User", "edit", "ALL"),
      hide: false,
    },
  },
  {
    name: "organization",
    show: "/organizations/:id",
    list: "/organizations",
    create: "/organizations/create",
    meta: {
      hide: true,
    },
  },
  {
    name: "subscriptions",
    list: "/subscriptions",
    show: "/subscriptions/:id",
    meta: {
      icon: <LightningIcon />,
      label: t("pages.subscription.title"),
      hasRole: hasRole("Subscriptions", "show"),
      hide: true,
    },
  },
  {
    name: "system",
    show: "/system",
    list: "/system",
    meta: {
      icon: <GearIcon />,
      label: t("src.App.system"),
      hasRole: hasRole("System", "edit", "ALL"),
      hide: true,
    },
  },
  {
    name: "media_theme_editor",
    list: "/theme-editor",
    meta: {
      parent: "media",
      label: t("src.App.themeEditor"),
      icon: <PaintBrushIcon />,
      featureFlag: FeatureFlags.ThemeEditor,
    },
  },
  {
    name: "feature_flags",
    show: "/flags",
    list: "/flags",
    create: "/flags/create",
    edit: "/flags/:id/edit",
    meta: {
      label: t("src.App.featureFlags"),
      icon: <ToggleLeftIcon />,
    },
  },
  {
    name: "manage_subscriptions",
    list: "/manage-subscriptions",
    create: "/manage-subscriptions",
    meta: {
      label: t("src.App.manageSubscriptions"),
      icon: <LightningIcon />,
      adminOnly: true,
    },
  },
];
